import { useEffect, useRef, useState } from 'react';
import { useConfirm } from '../components/confirm.js';
import { navigate } from '../platform/router.js';
import {
  applyImport,
  fetchImportPhotos as fetchPhotos,
  importPhotoProgress,
  previewImport,
  undoImport,
  type ImportReport,
  type PhotoOutcome,
  type PhotoRun,
  type UndoOutcome,
} from '../platform/import.js';
import { t } from '../platform/i18n.js';
import { useLanguage } from '../platform/use-language.js';

/**
 * The import screen — in-app import increment 3
 * (`docs/archive/in-app-import-design-v1_1.md` §2.3).
 *
 * THREE STATES, AND THE MIDDLE ONE IS THE POINT: choose → PREVIEW → result. Nothing
 * is written until the preview has been read and the confirm pressed, and that
 * confirm names the exact number of items (the destructive-action rule, applied to a
 * constructive act — "Import 1,940 items" is a sentence someone can disagree with;
 * "Import" is not).
 *
 * THE SCREEN DOES NOT GATE, THE SERVER DOES. Roles render, they do not permit
 * (standing rule): a non-admin can reach this screen and will be refused by the route
 * with a sentence, which is a better answer than a hidden button that leaves someone
 * wondering where the feature went.
 *
 * WHAT IS SHOWN IS WHAT WILL HAPPEN. The preview and the result render the same
 * report shape from the same server code — including the counts nobody enjoys reading:
 * rows skipped because they already exist, every rejected value with its row number,
 * and every populated column the profile does not map.
 */
/**
 * A determinate bar, because the numbers exist. "Add a visual progression when importing"
 * (30 Jul 2026) — the fetch was a spinner for minutes over ~1,935 photos with nothing to
 * watch and no way to tell progress from a hang.
 *
 * `<progress>` rather than a styled div: it is announced to a screen reader as a
 * measurable amount without any aria plumbing, and it cannot drift from the text beside it
 * because both read the same two numbers.
 */
function ImportProgress({ outstanding }: { outstanding: { total: number; fetched: number; pending: number } }): JSX.Element | null {
  if (outstanding.total === 0) return null;
  return (
    <span className="import-progress">
      <progress value={outstanding.fetched} max={outstanding.total} />{' '}
      <small>
        {String(outstanding.fetched)} of {String(outstanding.total)} photos
        {outstanding.pending > 0 ? ` · ${String(outstanding.pending)} left` : ' ✓'}
      </small>
    </span>
  );
}

export function ImportScreen(): JSX.Element {
  useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmEl, ask] = useConfirm();
  /** True only during the photo loop, so Cancel appears for that act and no other. */
  const [fetching, setFetching] = useState(false);
  const [stoppedLine, setStoppedLine] = useState<string | null>(null);
  /**
   * SECONDS SINCE THE APPLY STARTED, or null when nothing is running.
   *
   * ⚠️ REPORTED BECAUSE A GREY BUTTON IS NOT FEEDBACK (atelier, 1 Aug 2026: "no visual
   * progress feedback… I had to wait minutes"). Writing 4,000 items is genuinely slow and
   * it is ONE request, so there is no honest percentage to show — the transaction either
   * commits or it does not. What CAN be shown truthfully is that it is still working and
   * for how long, which is the difference between "frozen" and "busy". The same lesson as
   * the photo fetch, where a still bar read as a hang.
   */
  const [elapsed, setElapsed] = useState<number | null>(null);

  useEffect(() => {
    if (elapsed === null) return;
    const tick = setInterval(() => {
      setElapsed((n) => (n === null ? null : n + 1));
    }, 1000);
    return () => clearInterval(tick);
    // Deliberately keyed on whether a run is in flight, not on `elapsed` — depending on the
    // value would tear down and rebuild the interval every second.
  }, [elapsed === null]);
  const [problem, setProblem] = useState<{ message: string; detail?: string[] } | null>(null);
  const [preview, setPreview] = useState<ImportReport | null>(null);
  const [result, setResult] = useState<ImportReport | null>(null);
  const [photos, setPhotos] = useState<PhotoOutcome | null>(null);
  const [undone, setUndone] = useState<UndoOutcome | null>(null);
  /**
   * Outstanding photo work, read from the SERVER on mount so leaving and coming back does
   * not hide the Fetch button (field report, 30 Jul 2026). `null` = not asked yet.
   */
  const [outstanding, setOutstanding] = useState<{
    total: number;
    fetched: number;
    pending: number;
    runs: PhotoRun[];
  } | null>(null);
  /** Which file's photographs are being fetched right now, for the label. */
  const [fetchingRun, setFetchingRun] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const progress = await importPhotoProgress();
      if (progress.ok) {
        setOutstanding({
          total: progress.total,
          fetched: progress.fetched,
          pending: progress.pending,
          runs: progress.runs,
        });
      }
    })();
  }, []);

  /**
   * UNDO NAMES ITS EXACT TARGET IN THE CONFIRM (standing rule: destructive actions are
   * scoped to the visible context and name what they will do). The number comes from the
   * report on screen, so the sentence and the button cannot disagree.
   */
  const doUndo = async (report: ImportReport): Promise<void> => {
    const runId = report.importRunId;
    if (runId == null || busy) return;
    // `itemsToWrite` is what THIS run added. NOT `verified.items`, which is a
    // whole-tenant total — using it made the confirm offer to remove every item in the
    // catalogue, which is the opposite of naming your exact target.
    const count = report.plan.itemsToWrite;
    if (
      !(await ask({
        title: 'Undo this import?',
        body:
          `It will remove the ${String(count)} item(s) it added. ` +
          'Anything that has been checked out, edited, booked or flagged since will be KEPT, and listed. ' +
          'Nothing that existed before the import is touched.',
        confirmLabel: 'Undo import',
      }))
    ) {
      return;
    }
    setBusy(true);
    setProblem(null);
    const outcome = await undoImport(runId);
    if (outcome.ok) setUndone(outcome);
    else setProblem(outcome.detail ? { message: outcome.message, detail: outcome.detail } : { message: outcome.message });
    setBusy(false);
  };

  /**
   * A REF, NOT STATE. The loop reads this between rounds, and a state variable captured
   * when `doFetchPhotos` started would still read `false` however many times the button
   * was pressed — the closure would never see the update. This is the "prefilled values
   * are not evidence of user action" rule wearing its other face: a value the loop can
   * actually observe.
   */
  const cancelPhotos = useRef(false);

  const doFetchPhotos = async (runId?: string | null): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    /**
     * LOOPS UNTIL DONE, showing progress each round. The route fetches up to 500 photos per
     * call and answers with what is left, so ~1,935 imported photos take four rounds — and
     * a single await gave a spinner for minutes with nothing to watch ("add a visual
     * progression when importing").
     *
     * THE STALL GUARD IS THE LOAD-BEARING PART: photos that fail or are refused stay
     * pending forever, so "repeat while pending > 0" would spin without end, re-requesting
     * the same broken URLs. It stops the moment a round does not reduce `pending`, and what
     * is left is reported rather than retried.
     */
    cancelPhotos.current = false;
    setStoppedLine(null);
    setFetching(true);
    setFetchingRun(runId ?? null);
    let previousPending = Number.POSITIVE_INFINITY;
    let stopped = false;
    for (;;) {
      const outcome = await fetchPhotos(runId);
      if (!outcome.ok) {
        setProblem({ message: outcome.message });
        break;
      }
      setPhotos(outcome);
      setOutstanding((prev) => ({
        total: outcome.total,
        fetched: outcome.fetched,
        pending: outcome.pending,
        // The fetch reply carries a fresh breakdown when the API is new enough; otherwise
        // the previous one is kept rather than blanked, so the list does not flicker away
        // mid-run against an older server.
        runs: outcome.runs ?? prev?.runs ?? [],
      }));
      if (outcome.pending === 0 || outcome.pending >= previousPending) break;
      previousPending = outcome.pending;
      /**
       * CANCEL LANDS BETWEEN BATCHES, NOT INSIDE ONE (atelier, 31 Jul 2026: "there needs
       * to be a cancel fetch option for the import photo fetch"). Nearly two thousand
       * photographs is fifteen-odd minutes with the screen pinned open, and until now the
       * only way out was closing the tab.
       *
       * The batch already in flight is allowed to finish rather than aborted. Every photo
       * it stores is stamped `fetched_at` only AFTER the object is in R2, and the work is
       * idempotent by that stamp — so stopping between rounds loses nothing and pressing
       * Fetch again resumes exactly where this left off. Aborting mid-batch would buy a
       * few seconds and risk a half-written object for no gain.
       */
      if (cancelPhotos.current) {
        stopped = true;
        break;
      }
    }
    setFetching(false);
    setFetchingRun(null);
    if (stopped) {
      // Said out loud: a progress bar that simply stops looks like a hang, which is the
      // exact complaint that produced the batching in the first place.
      setStoppedLine('Fetching stopped. Nothing was lost — press “Fetch photos” to carry on where it left off.');
    }
    setBusy(false);
  };

  /** Any change to the inputs invalidates a preview: a report describing a file that
   * is no longer selected is worse than no report. */
  const resetReports = (): void => {
    setPreview(null);
    setResult(null);
    setProblem(null);
  };

  const runPreview = async (): Promise<void> => {
    if (busy || file === null) return;
    setBusy(true);
    resetReports();
    const outcome = await previewImport(file);
    if (outcome.ok) setPreview(outcome);
    else setProblem({ message: outcome.message, ...(outcome.detail ? { detail: outcome.detail } : {}) });
    setBusy(false);
  };

  const runApply = async (): Promise<void> => {
    if (busy || file === null || preview === null) return;
    setBusy(true);
    setProblem(null);
    setElapsed(0);
    const outcome = await applyImport(file);
    setElapsed(null);
    if (outcome.ok) {
      setResult(outcome);
      setPreview(null);
    } else setProblem({ message: outcome.message, ...(outcome.detail ? { detail: outcome.detail } : {}) });
    setBusy(false);
  };

  const report = result ?? preview;

  return (
    <section>
      {confirmEl}
      {/* 2m: the title becomes the quiet section label the Settings frames wear, with
          "administrators only" as its right-side hint (frame 2m). It stays a HEADING element —
          the e2e finds it by role — only its level and styling change; the Settings panel above
          owns the page's h2. */}
      <div className="import-head-row">
        <h3 className="settings-section-label">{t('import.heading')}</h3>
        <span className="import-head-hint">administrators only</span>
      </div>
      {/**
        * OUTSTANDING PHOTO WORK, ABOVE EVERYTHING AND BEFORE ANY FILE IS CHOSEN.
        *
        * It first lived inside `{report !== null && …}`, so it appeared only after checking
        * a file — and the whole point is that the work outstanding has nothing to do with
        * this visit's file (maintainer, 31 Jul 2026: "the remaining photo fetch button
        * should be visible without having to first need to do a check on a file"). The
        * server is asked on mount; if photos are pending, the button is simply there.
        */}
      {stoppedLine !== null && <p role="status">{stoppedLine}</p>}
      {outstanding !== null && outstanding.pending > 0 && (
        <div className="import-photos">
          <p>
            <button type="button" disabled={busy} onClick={() => void doFetchPhotos()}>
              {busy && fetchingRun === null
                ? 'Fetching…'
                : `Fetch all ${String(outstanding.pending)} remaining photo(s)`}
            </button>{' '}
            {fetching && (
              <button type="button" className="ghost-button" onClick={() => { cancelPhotos.current = true; }}>
                Cancel
              </button>
            )}
          </p>
          <ImportProgress outstanding={outstanding} />
          {/**
            * PER FILE, BECAUSE "N REMAINING" DOES NOT SAY WHOSE (atelier, 1 Aug 2026: "if
            * I'm working with multiple import files, I have no idea which one is fetching").
            *
            * Only files with something OUTSTANDING are listed — a finished import is not a
            * task, and leaving it on screen would make the list grow forever.
            *
            * The list is absent against an API one release behind, which sends no
            * breakdown. That degrades to the single button above rather than rendering an
            * empty section, because the bundle reaches Cloudflare before the API reaches
            * Fly on every release.
            */}
          {outstanding.runs.filter((r) => r.pending > 0).length > 1 && (
            <ul className="import-photo-runs">
              {outstanding.runs
                .filter((r) => r.pending > 0)
                .map((run) => (
                  <li key={run.runId ?? 'no-run'}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void doFetchPhotos(run.runId)}
                    >
                      {busy && fetchingRun === run.runId ? 'Fetching…' : `Fetch ${String(run.pending)}`}
                    </button>{' '}
                    {/* The no-run group is named as what it is, never left blank: those
                        photographs are real and belong to no uploaded file. */}
                    {run.filename ?? 'Not from an import file'}
                    <small>
                      {' '}
                      · {run.fetched} of {run.total} fetched
                    </small>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      <p>
        <small>{t('import.intro')}</small>
      </p>
      {/* THE SAFETY RULE AS A LEVEL-3 INFO BAND (redesign 7c): "nothing is overwritten" is the
          reason it is safe to press this, so it sits in a band at the top rather than as a grey
          tail clause under the heading. Copy inline in English, like the export panel's bands —
          the redesign bands are not run through the dictionary. */}
      <p className="info-band">
        <small>
          <strong>Nothing is ever overwritten.</strong> Anything already here is skipped and counted.
        </small>
      </p>

      {problem !== null && (
        <div role="alert">
          <p>{problem.message}</p>
          {problem.detail !== undefined && problem.detail.length > 0 && (
            <ul>
              {problem.detail.map((line) => (
                <li key={line}>
                  <small>{line}</small>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* 1. CHOOSE — just the file. The server recognises where it came from and
          says so in the preview, which is approved before anything is written.

          THE FILE INPUT IS A DASHED DROP AREA (frame 2m): the native input covers the box
          invisibly, so a click anywhere — or a dropped file — opens/sets it, while the name and
          the pill show beneath. It keeps an explicit `aria-label` so the input's accessible name
          stays "The CSV file" (the wrapping label's other text would otherwise swallow it), which
          is what `getByLabel` and drag-drop both resolve against. */}
      <h3 className="settings-section-label">{t('import.file')}</h3>
      <label className="import-drop">
        <input
          type="file"
          accept=".csv,text/csv"
          aria-label={t('import.file')}
          className="import-drop-input"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            resetReports();
          }}
        />
        <span className="import-drop-name">{file !== null ? file.name : 'No file chosen'}</span>
        <span className="import-drop-btn">Choose a file…</span>
      </label>
      <p>
        <small>{t('import.checkHint')}</small>
      </p>
      {/* CHECK THE FILE — the primary for the CHOOSE state (frame 2m), floating clear on a phone /
          in place on a desk, disabled until a file is chosen. Only while no report is on screen:
          once a preview or result renders, its own Import/Cancel take over below, and choosing a
          new file (which clears the report) brings this back. */}
      {report === null && (
        <div className="board-primary-act">
          <button type="button" className="btn-primary" disabled={busy || file === null} onClick={() => void runPreview()}>
            {busy && preview === null ? t('import.checking') : t('import.check')}
          </button>
        </div>
      )}

      {/* 2. PREVIEW / 3. RESULT — the same report, one of them already written. */}
      {report !== null && (
        <div className={result !== null ? 'import-report import-done' : 'import-report'}>
          <h3>{result !== null ? t('import.doneHeading') : t('import.previewHeading')}</h3>
          <p>
            <small>{t('import.recognisedAs', { profile: report.profileLabel })} — {report.detectedBy}</small>
          </p>
          <ul>
            <li>
              {t('import.rowsRead')}: <strong>{report.rowsInFile}</strong>
            </li>
            <li>
              {result !== null ? t('import.itemsAdded') : t('import.itemsToAdd')}:{' '}
              <strong>{report.plan.itemsToWrite}</strong>
            </li>
            {/* R3 made visible: the number nobody thinks to ask for, shown every time. */}
            <li>
              {t('import.skippedExisting')}: <strong>{report.plan.itemsSkippedExisting}</strong>
            </li>
            {report.plan.categoriesToCreate.length > 0 && (
              <li>
                {t('import.newCategories')}: {report.plan.categoriesToCreate.length}
              </li>
            )}
            {report.plan.locationsToCreate.length > 0 && (
              <li>
                {t('import.newLocations')}: {report.plan.locationsToCreate.length}
              </li>
            )}
            {report.plan.fieldsToCreate.length > 0 && (
              <li>
                {t('import.newFields')}: {report.plan.fieldsToCreate.map((f) => f.label).join(', ')}
              </li>
            )}
            {/**
              * THE PEOPLE, NAMED — not just counted. Every other line here is a number,
              * and for clients a number is not enough: this is the moment someone can
              * still say "that one should not be in here" before any personal data is
              * written. The count of garments each had out is what the atelier uses to
              * re-establish checkouts, since the import deliberately creates no bookings.
              */}
            {report.plan.clientsToCreate.length > 0 && (
              <li>
                Clients to create: {report.plan.clientsToCreate.length}
                <ul>
                  {report.plan.clientsToCreate.map((c) => (
                    <li key={c.name}>
                      {c.name}
                      {c.company !== null ? ` · ${c.company}` : ''}
                      {c.email !== null ? ` · ${c.email}` : ''} · {c.itemCount} item
                      {c.itemCount === 1 ? '' : 's'} out
                    </li>
                  ))}
                </ul>
                <small>No booking is created — re-check-out the ones that still matter.</small>
              </li>
            )}
            {report.plan.clientsExisting > 0 && (
              <li>Clients already known: {report.plan.clientsExisting}</li>
            )}
            {/**
              * IMPORTED, BUT WITHOUT THEIR CUSTODY. Named rather than counted, and shown
              * as an ALERT rather than a statistic: each line is a garment somebody is
              * physically holding that Mononoke now believes is on the rack. The atelier
              * fixes it by checking them out to the right person — a minute's work, but
              * only if they know which ones.
              */}
            {result?.custodySkipped !== undefined && result.custodySkipped.length > 0 && (
              <li role="alert">
                {result.custodySkipped.length} item(s) imported WITHOUT their custody — the
                custodian is not an active user here:
                <ul>
                  {result.custodySkipped.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <small>Add that person as a user, then check these out to them.</small>
              </li>
            )}
            {report.plan.custodyRows > 0 && (
              <li>
                {t('import.custodyRows')}: {report.plan.custodyRows}
              </li>
            )}
            {report.plan.photosToLink > 0 && (
              <li>
                {t('import.photosToLink')}: {report.plan.photosToLink}
              </li>
            )}
          </ul>

          {(report.plan.strayImported.length > 0 || report.plan.strayLocalCount > 0) && (
            <>
              <h4>
                {t('import.strays')} ({report.plan.strayImported.length + report.plan.strayLocalCount})
              </h4>
              <p>
                <small>{t('import.straysHint')}</small>
              </p>
              <ul>
                {report.plan.strayLocalCount > 0 && (
                  <li>
                    <small>{t('import.strayLocal', { count: String(report.plan.strayLocalCount) })}</small>
                  </li>
                )}
                {report.plan.strayImported.slice(0, 30).map((stray) => (
                  <li key={stray.externalRef}>
                    <small>
                      {stray.name} — {stray.externalRef}
                    </small>
                  </li>
                ))}
              </ul>
              {report.plan.strayImported.length > 30 && (
                <p>
                  <small>{t('import.andMore', { count: String(report.plan.strayImported.length - 30) })}</small>
                </p>
              )}
            </>
          )}

          {report.plan.codesSkipped.length > 0 && (
            <>
              <h4>{t('import.codesSkipped')} ({report.plan.codesSkipped.length})</h4>
              <p>
                <small>{t('import.codesSkippedHint')}</small>
              </p>
              <ul>
                {report.plan.codesSkipped.slice(0, 20).map((c) => (
                  <li key={c.code}>
                    <small>
                      {c.externalRef} — {c.code}
                    </small>
                  </li>
                ))}
              </ul>
            </>
          )}

          {report.rejects.length > 0 && (
            <>
              <h4>{t('import.rejects')} ({report.rejects.length})</h4>
              <p>
                <small>{t('import.rejectsHint')}</small>
              </p>
              <ul className="import-rejects">
                {report.rejects.slice(0, 50).map((r) => (
                  <li key={`${String(r.rowNumber)}-${r.column}-${r.value}`}>
                    <small>
                      {t('import.row')} {r.rowNumber} · {r.externalRef} · <strong>{r.column}</strong> ={' '}
                      {r.value === '' ? '—' : r.value} — {r.reason}
                    </small>
                  </li>
                ))}
              </ul>
              {report.rejects.length > 50 && (
                <p>
                  <small>{t('import.andMore', { count: String(report.rejects.length - 50) })}</small>
                </p>
              )}
            </>
          )}

          {report.unmapped.length > 0 && (
            <>
              <h4>{t('import.unmapped')}</h4>
              <p>
                <small>{t('import.unmappedHint')}</small>
              </p>
              <ul>
                {report.unmapped.map((u) => (
                  <li key={u.column}>
                    <small>
                      {u.column} — {u.count} {t('import.rowsWord')}
                    </small>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* The confirm names the exact count. Only ever shown for a PREVIEW. */}
          {elapsed !== null && (
            <p role="status" className="import-working">
              Writing {report.plan.itemsToWrite} item(s) — {elapsed}s elapsed. This is one
              transaction: it finishes or it changes nothing, so leaving this screen now
              would lose the run.
            </p>
          )}
          {result === null && (
            <p>
              <button type="button" disabled={busy || report.plan.itemsToWrite === 0} onClick={() => void runApply()}>
                {busy ? t('import.importing') : t('import.confirm', { count: String(report.plan.itemsToWrite) })}
              </button>{' '}
              <button type="button" disabled={busy} onClick={resetReports}>
                {t('common.cancel')}
              </button>
              {report.plan.itemsToWrite === 0 && (
                <>
                  {' '}
                  <small>{t('import.nothingToAdd')}</small>
                </>
              )}
            </p>
          )}

          {result !== null && (
            <p>
              <button type="button" onClick={() => navigate({ name: 'items' })}>
                {t('import.seeItems')}
              </button>{' '}
              {/* PHOTOS. §5 said the in-app import does not download images because a
                  1,900-image fetch does not belong in a request — it still does not.
                  This asks for one bounded batch and reports what came back, and the
                  button stays until `pending` reaches zero. */}
              {(result.plan.photosToLink > 0 || photos !== null) && (
                <>
                  {fetching && (
                    <button type="button" className="ghost-button" onClick={() => { cancelPhotos.current = true; }}>
                      Cancel
                    </button>
                  )}
                  <button type="button" disabled={busy} onClick={() => void doFetchPhotos()}>
                  {photos === null
                    ? `Fetch ${String(result.plan.photosToLink)} photo(s)`
                    : photos.pending > 0
                      ? `Fetch the remaining ${String(photos.pending)}`
                      : 'All photos fetched ✓'}
                  </button>
                </>
              )}{' '}
              {outstanding !== null && outstanding.total > 0 && <ImportProgress outstanding={outstanding} />}{' '}
              {/* UNDO. Only offered when the server returned a run id — without migration
                  018 there is no provenance, so there is nothing to undo and the button
                  must not pretend otherwise. */}
              {result.importRunId != null && undone === null && (
                <button type="button" disabled={busy} onClick={() => void doUndo(result)}>
                  Undo this import
                </button>
              )}
            </p>
          )}

          {photos !== null && (
            <p className="import-photos" role="status">
              {photos.fetched} fetched, {photos.pending} still to go
              {photos.failed > 0 && ` · ${String(photos.failed)} failed (they will be retried)`}
              {photos.refused > 0 && ` · ${String(photos.refused)} refused: not the atelier's own CDN`}
            </p>
          )}

          {undone !== null && (
            <div className="import-undone" role="status">
              <p>{undone.removed} item(s) removed.</p>
              {undone.kept.length > 0 && (
                <>
                  {/* NOT a footnote. A partly-undone import is a real state, and the
                      list of what stayed is the whole reason partial undo is acceptable. */}
                  <p>{undone.kept.length} item(s) were KEPT because they have been used since the import:</p>
                  <ul>
                    {undone.kept.map((item) => (
                      <li key={item.id}>
                        {item.name} — {item.reason}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
